import { useMutation, useQuery, useQueryClient } from 'react-query';
import { formatCustomDate } from '../../utils/formatCustomDate';
import { chamarOktonBank, enviarOktonBank } from '../oktonbank';

// Classificações do título no IFLEX. Vêm pelo módulo do OktonBank para não
// exigir que o usuário tenha também os módulos Centro de Custo, Departamento e
// Orientador liberados só para classificar um lançamento.

export interface OpcaoCentroCusto {
  id_cdc: string;
  nome_cdc: string;
}

export interface OpcaoDepartamento {
  id_dpt: string;
  nome_dpt: string;
}

// Direcionador é o nome de tela do Orientador de Operação (orientador_fin).
// O Plano de Contas DFC não fica no Orientador e sim em oriplacon, na linha
// geral — vem junto porque é ele que o Controla DRE exige.
export interface OpcaoDirecionador {
  id_ori: string;
  nome_ori: string;
  contacustodfc_pcc: string | null;
  item_pct: string | null;
  nome_pct: string | null;
}

export interface OpcaoCliente {
  id_cli: string;
  nome_cli: string;
  // Um dos dois vem preenchido, conforme pessoa física ou jurídica.
  cpf_cli: string | null;
  cnpj_cli: string | null;
}

// O documento do Cliente, seja ele CPF ou CNPJ.
export function documentoDoCliente(cliente?: OpcaoCliente): string {
  if (!cliente) return '';
  return (cliente.cpf_cli ?? cliente.cnpj_cli ?? '').replace(/\D/g, '');
}

// Valor sentinela dos seletores: pede o cadastro do registro com o nome
// digitado ao lado, sem sair da tela de Lançamentos.
export const CENTRO_CUSTO_NOVO = 'novo';
export const DEPARTAMENTO_NOVO = 'novo';
export const DIRECIONADOR_NOVO = 'novo';
export const CLIENTE_NOVO = 'novo';

// tipo_cli do IFLEX: 1 tem CPF (Pessoa Física), 2 tem CNPJ (Pessoa Jurídica).
export const TIPO_CLIENTE_PF = '1';
export const TIPO_CLIENTE_PJ = '2';

export interface EnderecoConsultaCnpj {
  cep: string | null;
  rua: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  municipio: number | null;
}

export interface ConsultaCnpj {
  encontrado: boolean;
  nome?: string;
  endereco?: EnderecoConsultaCnpj;
}

export interface ClienteExistente {
  existe: boolean;
  cliente: { id_cli: string; nome_cli: string } | null;
}

export interface NovoCliente {
  tipo: string;
  nome: string;
  documento: string;
  // Só se aplica à Pessoa Jurídica. Sem preencher, o Cliente nasce isento
  // (isentoie_cli), que é o que a maior parte do cadastro já faz.
  ie?: string;
  endereco?: EnderecoConsultaCnpj;
}

interface IResponseData<T> {
  rows: T[];
}

// Cadastro muda pouco e aparece em quase toda tela. Sem isto, sair de
// Lançamentos e voltar refaz todas as listas, e cada ida ao servidor custa
// uma requisição inteira. Toda gravação invalida a chave, então o número na
// tela nunca fica velho por causa desta janela.
const CADASTRO_STALE = 1000 * 60 * 5;

async function listar<T>(acao: string, params = {}): Promise<T[]> {
  // enviarOktonBank e nao chamarOktonBank: a busca de Cliente leva espaco e
  // acento, e o montador simples nao codifica o corpo.
  const dados = await enviarOktonBank<IResponseData<T>>(
    'OBCadastros',
    acao,
    params,
  );
  return Array.isArray(dados?.rows) ? dados.rows : [];
}

export function useCentrosCusto(habilitado = true) {
  return useQuery(
    ['oktonbank-centroscusto'],
    () => listar<OpcaoCentroCusto>('listarCentrosCusto'),
    { staleTime: CADASTRO_STALE, enabled: habilitado },
  );
}

export function useDepartamentos(habilitado = true) {
  return useQuery(
    ['oktonbank-departamentos'],
    () => listar<OpcaoDepartamento>('listarDepartamentos'),
    { staleTime: CADASTRO_STALE, enabled: habilitado },
  );
}

export function useDirecionadores(habilitado = true) {
  return useQuery(
    ['oktonbank-direcionadores'],
    () => listar<OpcaoDirecionador>('listarDirecionadores'),
    { staleTime: CADASTRO_STALE, enabled: habilitado },
  );
}

// Busca, não lista: em base de produção o cadastro de Clientes passa de
// milhares de linhas. Só consulta a partir de dois caracteres para não
// disparar uma ida ao servidor a cada tecla.
export function useClientes(busca: string) {
  const termo = busca.trim();
  return useQuery(
    ['oktonbank-clientes', termo],
    () => listar<OpcaoCliente>('buscarClientes', { busca: termo }),
    { enabled: termo.length >= 2, keepPreviousData: true },
  );
}

export function useConsultaCnpj(documento: string) {
  const digitos = documento.replace(/\D/g, '');
  return useQuery(
    ['oktonbank-cnpj', digitos],
    () =>
      chamarOktonBank<ConsultaCnpj>('OBCadastros', 'consultarCnpj', {
        cnpj: digitos,
      }),
    // O resultado da Receita não muda no meio da digitação: uma consulta por
    // CNPJ chega, sem refazer a cada foco no campo.
    { enabled: digitos.length === 14, staleTime: Infinity },
  );
}

// Avisa, ainda no formulário do "Novo Cliente", que o CPF/CNPJ digitado já
// pertence a outro Cliente - sem isso o cadastro rápido reaproveitava o
// Cliente existente por baixo (proteção contra duplo clique), vinculando o
// financeiro a um Cliente diferente do nome que a pessoa acabou de digitar,
// sem nenhum aviso na tela.
export function useClientePorDocumento(documento: string) {
  const digitos = documento.replace(/\D/g, '');
  return useQuery(
    ['oktonbank-cliente-documento', digitos],
    () =>
      chamarOktonBank<ClienteExistente>('OBCadastros', 'clientePorDocumento', {
        documento: digitos,
      }),
    { enabled: digitos.length === 11 || digitos.length === 14 },
  );
}

export function useCreateCliente() {
  const queryClient = useQueryClient();

  return useMutation(
    (cliente: NovoCliente) =>
      enviarOktonBank<{ id_cli: number }>('OBCadastros', 'cadastrarCliente', {
        tipo: cliente.tipo,
        nome: cliente.nome,
        documento: cliente.documento,
        ie: cliente.ie ?? '',
        ...(cliente.endereco ? { endereco: cliente.endereco } : {}),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('oktonbank-clientes');
      },
    },
  );
}

export function useCreateCentroCusto() {
  const queryClient = useQueryClient();

  return useMutation(
    (nome: string) =>
      enviarOktonBank<{ id_cdc: number }>(
        'OBCadastros',
        'cadastrarCentroCusto',
        { nome },
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('oktonbank-centroscusto');
      },
    },
  );
}

export function useCreateDepartamento() {
  const queryClient = useQueryClient();

  return useMutation(
    (nome: string) =>
      enviarOktonBank<{ id_dpt: number }>(
        'OBCadastros',
        'cadastrarDepartamento',
        { nome },
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('oktonbank-departamentos');
      },
    },
  );
}

// O Direcionador nasce com Tipo "Outros", Tipo de Venda "Normal" e Sugestão de
// Preço "Preço de Venda" — padrões resolvidos no back, a tela só pede o nome.
export function useCreateDirecionador() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ nome, planocontas }: { nome: string; planocontas?: string }) =>
      enviarOktonBank<{ id_ori: number }>(
        'OBCadastros',
        'cadastrarDirecionador',
        { nome, planocontas: planocontas ?? '' },
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('oktonbank-direcionadores');
      },
    },
  );
}

// Plano de Contas da estrutura DFC (Demonstração dos Fluxos de Caixa), usado
// na Transferência Bancária. item_pct é a numeração hierárquica (1.1.1.1).
//
// Só vêm as contas analíticas — as folhas da árvore. O nível de cima (ATIVO,
// ATIVO CIRCULANTE) só agrupa e não recebe lançamento, então não pode ser
// escolhido.
//
// `atual` é o que o registro em edição já tem gravado: vai junto para o caso
// de ser um nível que hoje não vale mais, senão o campo apareceria vazio e a
// classificação se perderia ao salvar.
export interface OpcaoPlanoContas {
  id_pct: string;
  item_pct: string;
  nome_pct: string;
}

export function usePlanosDFC(atual?: string | null, habilitado = true) {
  return useQuery(
    ['oktonbank-planosdfc', atual ?? ''],
    () => listar<OpcaoPlanoContas>('listarPlanosDFC', { atual: atual ?? '' }),
    { staleTime: CADASTRO_STALE, enabled: habilitado },
  );
}

// A estrutura DRE é a outra metade do Plano de Contas: o campo de DRE da
// Moeda sai dela, e os dois do Fluxo de Caixa saem da DFC.
export function usePlanosDRE(atual?: string | null) {
  return useQuery(
    ['oktonbank-planosdre', atual ?? ''],
    () => listar<OpcaoPlanoContas>('listarPlanosDRE', { atual: atual ?? '' }),
    { staleTime: CADASTRO_STALE },
  );
}

// Parâmetros da Filial que mudam o que a tela exige. Com o Controla DRE ligado
// o IFLEX cobra o Direcionador no título e o Plano de Contas no Direcionador e
// na Transferência — a tela marca os campos, o servidor é quem barra.
export interface ParametrosOktonBank {
  controladre: string;
}

export function useParametros() {
  return useQuery(
    ['oktonbank-parametros'],
    () => chamarOktonBank<ParametrosOktonBank>('OBCadastros', 'parametros'),
    { staleTime: 1000 * 60 * 5 },
  );
}

export function usaControlaDRE(parametros?: ParametrosOktonBank): boolean {
  return parametros?.controladre === '1';
}

// Moedas que podem ser depositadas: Dinheiro, Cheque de Terceiro e Outros.
// É o que cabe num envelope — Cartão, PIX, Vale e Adiantamento liquidam por
// conta própria, e a Moeda de Banco é o destino do depósito, não o que se
// deposita. Mesma regra da tela de Depósito do IFLEX.
export interface OpcaoMoedaPagamento {
  id_moe: string;
  nome_moe: string;
  // Dinheiro (01), Cheque de Terceiro (14) ou Outros (99): é o que diz se
  // o depósito é de papel ou de dinheiro.
  tipodocumento_tpt: string;
}

export function useMoedasPagamento(habilitado = true) {
  return useQuery(
    ['oktonbank-moedaspagamento'],
    () => listar<OpcaoMoedaPagamento>('listarMoedasPagamento'),
    { staleTime: CADASTRO_STALE, enabled: habilitado },
  );
}

// Caixa em que o lançamento cai. O back aceita vazio e resolve como o IFLEX
// sempre resolveu — a escolha da tela é um atalho, não um requisito.
// tipo_cxa: '1' Caixa Geral, '2' Caixa do Operador.
export interface OpcaoCaixa {
  id_cxa: string;
  tipo_cxa: string;
  dataabertura_cxa: string;
  nome_moe: string | null;
}

export const CAIXA_GERAL = '1';

// A data do lançamento faz parte da pergunta: um Caixa aberto hoje não recebe
// lançamento de ontem, então a lista muda conforme a data preenchida. Sem data
// vale hoje, que é o que o IFLEX assume.
export function useCaixas(data?: string) {
  return useQuery(
    ['oktonbank-caixas', data ?? ''],
    () => listar<OpcaoCaixa>('listarCaixas', { data: data ?? '' }),
    { staleTime: CADASTRO_STALE },
  );
}

// Como o Caixa aparece no seletor: o Geral se identifica sozinho, o do
// Operador precisa da data de abertura para o usuário saber qual é qual.
export function rotuloCaixa(caixa: OpcaoCaixa): string {
  const nome = caixa.nome_moe ?? `Caixa ${caixa.id_cxa}`;
  if (caixa.tipo_cxa === CAIXA_GERAL) {
    return nome;
  }
  const abertura = caixa.dataabertura_cxa
    ? formatCustomDate(
        caixa.dataabertura_cxa.slice(0, 10),
        'yyyy-MM-dd',
        'dd/MM/yyyy',
      )
    : '';
  return abertura ? `${nome} — aberto em ${abertura}` : nome;
}

// ─── Moeda ───
// A Moeda é a forma de pagamento do IFLEX. As do tipo Banco são as das Contas
// Bancárias, e é por elas que o extrato acha os movimentos.
// Só Moeda do tipo Banco: é a única que se vincula a uma Conta e acumula
// saldo. Os instrumentos que caem na Conta — PIX, Cartão — e as Moedas de
// Caixa continuam sendo cadastrados pelo IFLEX.
export interface Moeda {
  id_moe: string;
  nome_moe: string;
  principal_moe: string;
  visivel_moe: string;
  banco_moe: string | null;
  // Três papéis distintos: planocontas_moe classifica na DRE, e os dois de
  // DFC recebem o saldo no início e no fim do período no Fluxo de Caixa.
  // Até a versão anterior o campo da DRE acumulava também o saldo inicial.
  planocontas_moe: string | null;
  planocontasdfcinicial_moe: string | null;
  planocontasdfcfinal_moe: string | null;
  nome_ban: string | null;
  nomeplanodre_moe: string | null;
  nomeplanoinicial_moe: string | null;
  nomeplanofinal_moe: string | null;
  // A Conta em que a Moeda liquida, quando já vinculada.
  descricao_cts: string | null;
}

export interface NovaMoeda {
  id?: string;
  nome: string;
  banco?: string;
  planocontas?: string;
  planocontasdfcinicial?: string;
  planocontasdfcfinal?: string;
  principal?: boolean;
  visivel?: boolean;
}

export function useMoedas() {
  return useQuery(['oktonbank-moedas'], () => listar<Moeda>('listarMoedas'), {
    staleTime: CADASTRO_STALE,
  });
}

export function useSaveMoeda() {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: NovaMoeda) =>
      enviarOktonBank<{ id_moe: number }>('OBCadastros', 'gravarMoeda', {
        id: payload.id ?? '',
        nome: payload.nome,
        banco: payload.banco ?? '',
        planocontas: payload.planocontas ?? '',
        planocontasdfcinicial: payload.planocontasdfcinicial ?? '',
        planocontasdfcfinal: payload.planocontasdfcfinal ?? '',
        principal: payload.principal ? '1' : '0',
        visivel: payload.visivel ? '1' : '0',
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('oktonbank-moedas');
        queryClient.invalidateQueries('oktonbank-moedaspagamento');
        queryClient.invalidateQueries('contas-bancarias');
      },
    },
  );
}

// ─── Cadastro pela tela própria ───
// Diferente dos useCreate* acima, que são o atalho do meio do lançamento: lá o
// nome repetido reaproveita o registro, aqui é erro e dá para editar.

export function useSaveCentroCusto() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, nome }: { id?: string; nome: string }) =>
      enviarOktonBank<{ id_cdc: number }>('OBCadastros', 'gravarCentroCusto', {
        id: id ?? '',
        nome,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('oktonbank-centroscusto');
      },
    },
  );
}

export function useSaveDepartamento() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, nome }: { id?: string; nome: string }) =>
      enviarOktonBank<{ id_dpt: number }>('OBCadastros', 'gravarDepartamento', {
        id: id ?? '',
        nome,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('oktonbank-departamentos');
      },
    },
  );
}

// Excluir recusa registro em uso: o back procura quem ainda aponta para ele
// antes de apagar, porque essas tabelas não têm chave estrangeira.
function useRemover(acao: string, invalida: string) {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => enviarOktonBank('OBCadastros', acao, { id }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(invalida);
      },
    },
  );
}

export function useDeleteMoeda() {
  return useRemover('removerMoeda', 'oktonbank-moedas');
}

export function useDeleteCentroCusto() {
  return useRemover('removerCentroCusto', 'oktonbank-centroscusto');
}

export function useDeleteDepartamento() {
  return useRemover('removerDepartamento', 'oktonbank-departamentos');
}

export function useDeleteDirecionador() {
  return useRemover('removerDirecionador', 'oktonbank-direcionadores');
}

export function useSaveDirecionador() {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      id,
      nome,
      planocontas,
    }: {
      id?: string;
      nome: string;
      planocontas?: string;
    }) =>
      enviarOktonBank<{ id_ori: number }>('OBCadastros', 'gravarDirecionador', {
        id: id ?? '',
        nome,
        planocontas: planocontas ?? '',
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('oktonbank-direcionadores');
      },
    },
  );
}
